import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UsermanagementListAddUserComponent } from './usermanagement-list-add-user/usermanagement-list-add-user.component';
import { UsermanagementListOverridePasswordComponent } from './usermanagement-list-override-password/usermanagement-list-override-password.component';
import { AuthService } from 'src/app/services/auth.service';
import { User } from 'src/app/models/user.model';
import { RequestService } from 'src/app/services/request.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { UserCredentials } from 'src/app/models/Credentials.model';
import { UsermanagementListChangeEmailComponent } from './usermanagement-list-change-email/usermanagement-list-change-email.component';
import { OverrideEmail } from 'src/app/models/OverrideEmail.model';
import { AuthenticationResponse } from 'src/app/models/authenticationresponse.model';
import { ModalDialog } from 'src/app/Modal/modal';

@Component({
  selector: 'app-usermanagement-list',
  templateUrl: './usermanagement-list.component.html',
  styleUrls: ['./usermanagement-list.component.scss']
})
export class UsermanagementListComponent implements OnInit, OnDestroy {
  logedInUser: AuthenticationResponse;
  displayedColumns;
  addButtonText: string;
  users: User[];

  usermanagement = {
    title: 'User management', addButtonCompanyAdmin: 'Add new company admin', addButtonUser: 'Add New User',
    col1Titel: 'E-Mail', col2Titel: 'Company', col3Titel: 'Role', col4Titel: 'Actions',
    action1: 'Activate user', action2: 'Deactivate user', action3: 'Override password', action4: 'Change E-Mail', action5: 'Delete user',
    deleteDialog: 'Do you really want to delete this user?'
  }

  private unscubscribe$ = new Subject<void>();

  constructor(private dialog: MatDialog, private authUser: AuthService, private requestService: RequestService) {
    this.displayedColumns = ['email', 'company', 'role', 'actions'];
    this.logedInUser = this.authUser.getCurrenAuthenticationResponse();

    this.addButtonText = this.usermanagement.addButtonUser;
    if (this.logedInUser.roleId === 1) {
      this.addButtonText = this.usermanagement.addButtonCompanyAdmin;
    }
  }

  ngOnInit() {
    this.loadUsers();
  }

  overridePassword(user: User) {
    const dialogRef = this.dialog.open(UsermanagementListOverridePasswordComponent,
      {
        width: '420px',
        data: {
          email: user.email,
        }
      });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.save) {
        let credential: UserCredentials = new UserCredentials();
        credential.email = result.data.email;
        credential.password = result.data.password;
        this.requestService.overridePassword(credential).pipe(takeUntil(this.unscubscribe$))
          .subscribe(resp => {
            this.loadUsers();
          });
      }
    })
  }

  overrideEmail(user: User) {
    const dialogRef = this.dialog.open(UsermanagementListChangeEmailComponent,
      {
        width: '420px',
        data: {
          email: user.email,
          companyId: user.companyId
        }
      });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.save) {
        let emails: OverrideEmail = new OverrideEmail();
        emails.oldEmail = result.data.email;
        emails.newEmail = result.data.newEmail;
        emails.roleId = this.logedInUser.roleId;
        emails.companyId = this.logedInUser.companyId;

        this.requestService.overrideEmail(emails).pipe(takeUntil(this.unscubscribe$))
          .subscribe(resp => {
            this.setUserList(resp);
          });
      }
    });
  }

  activateUser(user: User) {
    this.requestService.maxActiveUsersReached(user.companyId).pipe(takeUntil(this.unscubscribe$))
      .subscribe((maxActiveUsersReached: boolean) => {
        if (maxActiveUsersReached === false) {
          let credential: UserCredentials = new UserCredentials();
          credential.email = user.email;
          credential.isBusinessActive = true;
          this.requestService.setUserIsBusinessActive(credential).pipe(takeUntil(this.unscubscribe$))
            .subscribe(resp => {
              this.loadUsers();
            });
        } else {
          this.dialog.open(ModalDialog, {
            data: {
              title: 'Information',
              message: "The limit of active users has been reached, this action can't be performed.",
              showCancel: false,
              saveButtonText: 'Okay'
            }
          });
        }
      });
  }

  deactivateUser(user: User) {
    let credential: UserCredentials = new UserCredentials();
    credential.email = user.email;
    credential.isBusinessActive = false;
    this.requestService.setUserIsBusinessActive(credential).pipe(takeUntil(this.unscubscribe$))
      .subscribe(resp => {
        this.loadUsers();
      });
  }

  deleteUserDialog(user: User) {
    const dialogRef = this.dialog.open(ModalDialog, {
      data: {
        title: 'Warning',
        message: this.usermanagement.deleteDialog,
        showCancel: true,
        cancelButtonText: 'Cancel',
        saveButtonText: 'Okay'
      }
    });

    dialogRef.afterClosed()
      .subscribe(result => {
        if (result) {
          this.deleteUser(user);
        } else {
          // do nothing just close the modal
        }
      });
  }

  deleteUser(user: User) {
    this.requestService.deleteUser(user.id).pipe(takeUntil(this.unscubscribe$))
      .subscribe(resp => {
        this.loadUsers();
      });
  }

  loadUsers() {
    this.requestService.getUsers(this.logedInUser.roleId, this.logedInUser.companyId, this.logedInUser.userId).pipe(takeUntil(this.unscubscribe$))
      .subscribe(resp => {
        this.setUserList(resp);
      });
  }

  setUserList(userList: User[]) {
    if (userList) {
      this.users = userList.sort(function (ob1, ob2) {
        if (ob1.companyId > ob2.companyId) {
          return 1;
        } else if (ob1.companyId < ob2.companyId) {
          return -1;
        }

        // Else go to the 2nd item
        if (ob1.email < ob2.email) {
          return -1;
        } else if (ob1.email > ob2.email) {
          return 1
        } else { // nothing to split them
          return 0;
        }
      });
    } else {
      this.users = [];
    }
  }

  createUser() {
    const dialogRef = this.dialog.open(UsermanagementListAddUserComponent,
      {
        width: '30vw',
        height: '75vh',
        data: {
          user: this.logedInUser
        }
      });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.save) {
        this.requestService.createUser(result.data).pipe(takeUntil(this.unscubscribe$))
          .subscribe(resp => {
            this.loadUsers();
          }, error => {
            //
          });
      }
    })
  }

  ngOnDestroy(): void {
    this.unscubscribe$.next();
    this.unscubscribe$.complete();
  }
}